import { hasFeature } from '@precert/has-feature';
import { FormSchema, Lookups } from '@precert/validation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Col, FormGroup, Row } from 'reactstrap';

import { useConfig } from '../../../../_components/config';
import { DateField } from '../../../../_components/date-field';
import { DateFieldInfo } from '../../../../_components/date-field/date-with-modal';
import InputField from '../../../../_components/input-field';
import SelectField from '../../../../_components/select-components/select-field';
import type { WizardStepProps } from '../wizard';

import { valuesCodeArray } from './common';
import DiagnosisCodes from './diagnosis-codes';
import MVPDiagnosisCodes from './diagnosis-codes-mvp';
import { PrecertCodeField } from './step-new-request';

const { PlacesOfService, ServiceTypes, QuantityTypes, BodyLocation } = Lookups;
const ServiceTypesOptions = valuesCodeArray(ServiceTypes);
const QuantityTypesOptions = valuesCodeArray(QuantityTypes);
const PlacesOfServiceOptions = valuesCodeArray(PlacesOfService);
const BodyLocationOptions = valuesCodeArray(BodyLocation);

export interface StepDetailInformationProps {
  index: number;
}

export interface StepDetailInformationFormData {
  authorizingVaFacility: string;
  diagnosisCodes: string[];
  bodyLocation: string;
  quantity: number;
  quantityType: string;
  placeOfService: string;
  dateOfService: string;
  serviceType: string;
}

export const StepDetailInformationSchema = FormSchema().pick([
  'diagnosisCodes',
  'procedureCode',
  'procedureCodesList',
  'bodyLocation',
  'quantity',
  'quantityType',
  'infusionCode',
  'serviceType',
  'dateOfService',
  'placeOfService',
  'primaryDiagnosis',
  'secondaryDiagnosis',
]);

export const diagnosisCodeRegex = /^[a-z0-9.\s]+$/i;

export const StepDetailInformation = () => {
  const { infusionProcedureCodes } = useConfig();
  const { setValue } = useFormContext();
  const { features } = useConfig();
  const procedureCode = useWatch({ name: 'procedureCode' });
  const requestType = useWatch({ name: 'requestType' });
  const isInpatient = requestType?.toLowerCase() === 'inpatient';
  const enableInfusionCode = infusionProcedureCodes?.includes(procedureCode);
  return (
    <>
      <h2>Diagnosis</h2>
      <Row>
        <FormGroup>
          {hasFeature('diagnosis-upgrade', features) ? (
            <DiagnosisCodes />
          ) : (
            <MVPDiagnosisCodes />
          )}
        </FormGroup>
      </Row>
      <hr aria-hidden="true" />
      <h2>Procedure</h2>
      <Row>
        <Col lg={6}>
          <FormGroup>
            <PrecertCodeField />
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup>
            <SelectField
              label="Body Location"
              name="bodyLocation"
              options={BodyLocationOptions}
            />
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup>
            <InputField
              required
              label="Quantity"
              name="quantity"
              type="number"
            />
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup>
            <SelectField
              required
              label="Quantity Type"
              name="quantityType"
              options={QuantityTypesOptions}
              onChange={(val) => {
                setValue('quantityTypeCode', val.code);
              }}
            />
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup>
            <InputField
              name="infusionCode"
              label="Drug Name/Description"
              disabled={!enableInfusionCode}
              type="text"
              id="infusionCode"
              aria-label="Drug Name/Description"
              information="Please enter a J-Code, Name, or NDC# if known."
            />
          </FormGroup>
        </Col>
      </Row>
      <hr aria-hidden="true" />
      <h2>Service Details</h2>
      <Row>
        <Col lg={6}>
          <FormGroup>
            <SelectField
              required={isInpatient}
              label="Service Type"
              name="serviceType"
              onChange={(val) => {
                setValue('serviceTypeCode', val.code);
              }}
              options={ServiceTypesOptions}
            />
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup>
            {hasFeature('date-of-service-info', features) ? (
              <DateFieldInfo
                required
                label="Date Of Service"
                name="dateOfService"
                information="In the event the date of service submitted for a precertification required service has change after the initial precertification submission to the VA, that new date of service must be provided to VA utilizing the link contained in your submission email."
              />
            ) : (
              <DateField
                required
                label="Date Of Service"
                name="dateOfService"
              />
            )}
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup>
            <SelectField
              required
              label="Place Of Service"
              name="placeOfService"
              options={PlacesOfServiceOptions}
              onChange={(val) => {
                setValue('placeOfServiceCode', val.code);
              }}
            />
            CPT copyright 2024 American Medical Association. All rights
            reserved.
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};

export const stepDetailInformation: WizardStepProps = {
  title: 'Precertification Detail',
  schema: StepDetailInformationSchema,
  element: <StepDetailInformation />,
  path: 'precertification-detail',
};
